import { useEffect, useId, useMemo, useRef, type ReactElement } from 'react';
import { FloatingLabel, Form } from 'react-bootstrap';
import { AsyncTypeahead, Highlighter, Menu, MenuItem, useHint } from 'react-bootstrap-typeahead';
import type TypeaheadCore from 'react-bootstrap-typeahead/types/core/Typeahead';
import type { TypeaheadInputProps } from 'react-bootstrap-typeahead/types/types';
import { observer } from 'mobx-react-lite';

import { ReferenceModel } from './ReferenceModel';
import { normalizeSource, type SourceProp } from './referenceSource';
import './EditReference.css';

// What react-bootstrap-typeahead's `options` array accepts. The
// library types it as `string | Record<string, any>`; we can't mirror
// that verbatim because we avoid explicit `any`, and the obvious
// substitute `Record<string, unknown>` doesn't admit plain interface
// types under strict TypeScript — they lack the index signature.
// `string | object` is the constraint callers' domain types actually
// meet, and `object` is structurally assignable to the library's
// `Record<string, any>` at the call site, so no cast is needed when
// passing through.
export type ReferenceItem = string | object;

export interface EditReferenceProps<T extends ReferenceItem> {
  readonly label: string;
  readonly value: T | null;
  readonly onChange: (value: T | null) => void;
  // Where the options come from. A capability-typed `ReferenceSource`
  // (static / eager / search) drives the behavior; a bare array or
  // zero-arg loader are the legacy forms, normalized to static / eager.
  // For the loader / search closures, pass a stable reference — a fresh
  // closure each render rebuilds the model and re-fetches.
  readonly source: SourceProp<T>;
  // Stable identifier per item. Used for React keys, to match the
  // controlled `value` against the loaded items (since `value` may be a
  // fresh object that doesn't share identity with what was fetched), and
  // for sentinel-label resolution.
  readonly getKey: (item: T) => string | number;
  readonly getLabel: (item: T) => string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Optional menu-item renderer. Defaults to a <Highlighter> on the
  // label. Wrappers pass this for a multi-column / table-like row layout
  // (e.g. name + zone). The `query` arg is the current input text, for
  // re-using <Highlighter search={query}>.
  readonly renderMenuItemChildren?: (item: T, query: string) => ReactElement;
}

// Debounce before a typed query reaches the source (server requests for
// the `search` kind; cheap in-memory filtering otherwise).
const SEARCH_DELAY_MS = 250;

/**
 * Searchable type-to-filter dropdown for LOV-backed reference fields,
 * wrapped in a Bootstrap FloatingLabel so it matches the other Edit*
 * controls. Always renders `AsyncTypeahead`; the behavior (client-side
 * filter vs debounced server-side search, with or without a load step)
 * is driven entirely by the `source` kind via {@link ReferenceModel}.
 *
 * Generic in one type only: `T` is the item shape. `value` / `onChange`
 * deal in `T | null`; callers that need wire-format strings translate at
 * their boundary.
 *
 * `flip` repositions the menu above the field when there's no room
 * below; `highlightOnlyResult` lets Enter accept a unique match. The
 * first matching option renders as ghost text via `useHint` — the
 * wrapping `<Hint>` would insert a flex div between `.form-floating` and
 * `.form-control` and break Bootstrap's adjacency-selector floating-label
 * rules, so we render the hint input as a sibling of `Form.Control`
 * inside the FloatingLabel ourselves. `selectHint` accepts the hint on
 * Tab as well as the library's default Enter. `clearButton` resets an
 * optional field to null without retyping.
 *
 * On blur, leftover unmatched text (value still null) is cleared via
 * `instanceRef.clear()` — standard combobox UX.
 *
 * The menu is rendered ourselves (`renderMenu`) so a "Showing N of X —
 * load more" footer can drive the model's paging in both client and
 * server modes; `paginate={false}` disables the library's own paginator
 * since the model already slices `options` to the current page.
 */
const EditReferenceInner = <T extends ReferenceItem>({
  label,
  value,
  onChange,
  source,
  getKey,
  getLabel,
  mandatory = false,
  disabled = false,
  renderMenuItemChildren,
}: EditReferenceProps<T>): ReactElement => {
  const id = useId();
  const floatingLabel = mandatory ? `${label} *` : label;
  const instanceRef = useRef<TypeaheadCore>(null);

  const model = useMemo(() => new ReferenceModel<T>(normalizeSource(source), getKey, getLabel), [source, getKey, getLabel]);

  useEffect(() => {
    model.syncValue(value);
  }, [model, value]);

  // Read everything the render (and the renderMenu closure) needs off the
  // model here, in the observer's own render, so MobX tracks the reads.
  const options = model.options;
  const selected = model.selectedFor(value);
  const { isLoading, emptyLabel, canLoadMore, shownCount, filteredCount } = model;

  return (
    <AsyncTypeahead
      id={id}
      clearButton
      labelKey={(opt) => getLabel(opt as T)}
      options={[...options]}
      selected={[...selected]}
      onChange={(sel) => onChange(model.handleChange(sel as T[]))}
      // The library applies a default `filterBy` to `options` unless one
      // is given; the model already produces exactly the rows to show, so
      // disable it — otherwise it re-filters by the input text (e.g. a
      // selected value's label) and can empty the menu.
      filterBy={() => true}
      // `onSearch` only fires on a typed, non-empty query (the library
      // skips empties and never fires it for a selection). An empty input
      // — backspaced or cleared — comes through `onInputChange` instead,
      // where we restore the initial set.
      onSearch={model.search}
      onInputChange={(text) => {
        if (text === '') model.resetToInitial();
      }}
      isLoading={isLoading}
      useCache={false}
      minLength={0}
      delay={SEARCH_DELAY_MS}
      paginate={false}
      emptyLabel={emptyLabel}
      flip
      // Position the menu with a fixed (viewport-relative) strategy so it escapes any `overflow: hidden` ancestor — e.g. a
      // DetailCard, whose clip (for its rounded header band) would otherwise cut the dropdown off inside the card body.
      positionFixed
      highlightOnlyResult
      disabled={disabled}
      selectHint={(shouldSelect, e) => e.key === 'Tab' || shouldSelect}
      // The library's forwardRef overrides any `instanceRef` prop with
      // the value of `ref` (even when none is passed), so `ref=` is the
      // only working path.
      ref={instanceRef}
      onBlur={(e) => {
        // Typed-but-unresolved text leaves value null while the input
        // still shows the text. Clear it so the visible state matches
        // the value (empty = nothing selected), and reset the list to the
        // initial set so a refocus doesn't reopen the abandoned search.
        if (value === null && e.target.value !== '') {
          instanceRef.current?.clear();
          model.resetToInitial();
        }
      }}
      renderMenu={(results, menuProps, state) => {
        const { paginationText, newSelectionPrefix, ...rest } = menuProps;
        // No rows: render an empty Menu so its own `emptyLabel` shows.
        // (A falsy `{canLoadMore && …}` child still counts toward
        // `Children.count`, which would suppress that fallback.)
        if (results.length === 0) {
          return <Menu {...rest} emptyLabel={emptyLabel} />;
        }
        return (
          <Menu {...rest} emptyLabel={emptyLabel}>
            {results.map((option, idx) => (
              <MenuItem key={String(getKey(option as T))} option={option} position={idx}>
                {renderMenuItemChildren ? (
                  renderMenuItemChildren(option as T, state.text)
                ) : (
                  <Highlighter search={state.text}>{getLabel(option as T)}</Highlighter>
                )}
              </MenuItem>
            ))}
            {canLoadMore && (
              <>
                <Menu.Divider />
                <button type="button" className="dropdown-item text-center small text-muted" onClick={() => model.loadMore()}>
                  Showing {shownCount} of {filteredCount} — load more
                </button>
              </>
            )}
          </Menu>
        );
      }}
      renderInput={(inputProps) => <FloatingLabelWithHint id={id} label={floatingLabel} placeholder={label} mandatory={mandatory} inputProps={inputProps} />}
    />
  );
};

// `observer` preserves the generic signature in mobx-react-lite v4, so
// `<EditReference<T> />` call sites keep their type parameter.
export const EditReference = observer(EditReferenceInner);

interface FloatingLabelWithHintProps {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly mandatory: boolean;
  readonly inputProps: TypeaheadInputProps;
}

// Renders the typeahead's input inside FloatingLabel, plus the ghost-hint
// input as a sibling — both direct children of `.form-floating` so
// Bootstrap's `> .form-control` adjacency rules still apply. Its own
// component because `useHint()` is a hook; calling it inside `renderInput`
// directly would violate React's rules of hooks.
const FloatingLabelWithHint = ({ id, label, placeholder, mandatory, inputProps }: FloatingLabelWithHintProps) => {
  const { hintRef, hintText } = useHint();
  const { inputRef, referenceElementRef, inputClassName, value, ...rest } = inputProps;
  return (
    <FloatingLabel controlId={id} label={label}>
      <Form.Control
        {...rest}
        // Typeahead's internal input value is always a string; its
        // HTMLInputElement-derived type widens it to include readonly
        // string[], which Form.Control's prop type rejects. Narrow at
        // the boundary.
        value={value as string | undefined}
        className={inputClassName}
        placeholder={placeholder}
        required={mandatory}
        ref={(el: HTMLInputElement | null) => {
          inputRef(el);
          referenceElementRef(el);
        }}
      />
      <input
        ref={hintRef}
        aria-hidden
        readOnly
        tabIndex={-1}
        value={hintText}
        className="rbt-input-hint"
        // Mirrors the inline styles `<Hint>` applies internally — the
        // hint floats over the input visually, doesn't take pointer
        // events, and copies font/padding from the input via useHint's
        // effect so the ghost text aligns with what's typed.
        style={{
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          boxShadow: 'none',
          color: 'rgba(0, 0, 0, 0.54)',
          left: 0,
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          width: '100%',
        }}
      />
    </FloatingLabel>
  );
};
